import { useEffect, useRef } from 'react'
import * as pc from 'playcanvas'

export type PcCanvasEngineInitializer = (canvas: HTMLCanvasElement) => () => void

export function createPcApplication(canvas: HTMLCanvasElement): pc.Application {
  const app = new pc.Application(canvas, {
    graphicsDeviceOptions: { antialias: false },
  })
  app.setCanvasFillMode(pc.FILLMODE_FILL_WINDOW)
  app.setCanvasResolution(pc.RESOLUTION_AUTO)
  if (typeof window !== 'undefined') {
    app.graphicsDevice.maxPixelRatio = Math.min(window.devicePixelRatio ?? 1, 1.5)
  }
  return app
}

export function configurePcPostProcessing(
  app: pc.Application,
  cameraEntity: pc.Entity,
): pc.CameraFrame {
  if (cameraEntity.camera) cameraEntity.camera.toneMapping = pc.TONEMAP_ACES
  app.scene.exposure = 0.62

  const frame = new pc.CameraFrame(app, cameraEntity.camera!)
  frame.rendering.toneMapping = pc.TONEMAP_ACES
  frame.rendering.samples = 4
  frame.bloom.intensity = 0.012
  frame.bloom.blurLevel = 16
  frame.vignette.intensity = 0.22
  frame.vignette.inner = 0.55
  frame.vignette.outer = 1.25
  frame.vignette.curvature = 0.6
  frame.ssao.type = pc.SSAOTYPE_NONE
  frame.ssao.intensity = 0.5
  frame.ssao.radius = 1.2
  frame.ssao.samples = 10
  frame.ssao.power = 6
  frame.ssao.blurEnabled = true
  frame.grading.enabled = true
  frame.grading.saturation = 1.08
  frame.grading.contrast = 1.06
  frame.colorEnhance.enabled = true
  frame.colorEnhance.shadows = 0.05
  frame.colorEnhance.vibrance = 0.15
  frame.update()
  return frame
}

export function DeferredPcCanvas({
  initializeEngine,
}: {
  initializeEngine: PcCanvasEngineInitializer
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  // Engine ownership is fixed for one canvas mount. Keeping the initial
  // initializer avoids tearing down a live world because a parent rerendered.
  const initializeRef = useRef(initializeEngine)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    // React StrictMode mounts, unmounts, and remounts effects in dev. Deferring
    // one frame prevents the throwaway mount from starting asset decode work.
    let disposed = false
    let cleanup: (() => void) | null = null
    const rafId = requestAnimationFrame(() => {
      if (disposed) return
      cleanup = initializeRef.current(canvas)
    })
    return () => {
      disposed = true
      cancelAnimationFrame(rafId)
      cleanup?.()
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      style={{ width: '100%', height: '100%', display: 'block' }}
    />
  )
}
